import { FOLLOW_UP_VISITS as ALLIANCE_VISITS } from '../alliance/allianceModel';
import {
  FOLLOW_UP_VISITS as COMPASS_VISITS,
  formProgress,
  instanceKey,
} from '../compass/compassModel';
const PIPELINE_PHASES = new Set(['enrollment', 'preop', 'surgery', 'exit']);

function procedureTokens(procedure) {
  if (Array.isArray(procedure)) return procedure.flatMap(procedureTokens);
  if (procedure && typeof procedure === 'object') {
    return procedureTokens(procedure.procedure || procedure.key || procedure.token || '');
  }
  const text = String(procedure || '').trim();
  return text ? [text] : [];
}

/** True when a scheduled procedure is Coflex. The study list uses enrollment, not this check. */
export function isAllianceEligibleProcedure(procedure) {
  return procedureTokens(procedure).some((token) => /coflex/i.test(token));
}

/** Compass covers a scheduled spine surgery. It is not limited to Coflex. */
export function isCompassEligibleProcedure(procedure) {
  return procedureTokens(procedure).length > 0;
}

function stagesFor(visits, { exitAfter } = {}) {
  const stages = [
    { id: 'enrollment', label: 'Enrollment', kind: 'enrollment' },
    { id: 'preop', label: 'Pre-op', kind: 'preop', withinDays: 30 },
    { id: 'surgery', label: 'Surgery', kind: 'surgery' },
    ...visits.filter((visit) => visit.fromDay != null && visit.throughDay != null).map((visit, index, visitsInOrder) => ({
      id: visit.id,
      label: visit.label,
      kind: 'followup',
      fromDay: visit.fromDay,
      throughDay: visit.throughDay,
      priorIds: visitsInOrder.slice(0, index).map((item) => item.id),
    })),
  ];
  if (exitAfter) {
    stages.push({
      id: 'exit',
      label: 'Study exit',
      kind: 'exit',
      afterId: exitAfter,
    });
  }
  return stages;
}

const PIPELINES = {
  alliance: {
    id: 'alliance',
    isEligible: isAllianceEligibleProcedure,
    stages: stagesFor(ALLIANCE_VISITS, { exitAfter: 'month-24' }),
  },
  compass: {
    id: 'compass',
    isEligible: isCompassEligibleProcedure,
    stages: stagesFor(COMPASS_VISITS),
  },
};

/** A study with no entry keeps the ordinary patient list. */
export function pipelineForStudy(studyId) {
  return PIPELINES[studyId] || null;
}

export function parseSurgeryDay(value) {
  const match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return startOfDay(next);
}

function daysAfterSurgery(surgery, today) {
  const ms = startOfDay(today).getTime() - surgery.getTime();
  return Math.round(ms / 86400000);
}

function defaultPercent(forms, form, visitId) {
  const saved = forms?.[instanceKey(form.id, visitId)] || {};
  return formProgress(form.fields, saved.values || {}, saved.repeats || {}).percent;
}

function sectionComplete(forms, formList, visitId, percentFor) {
  if (!formList?.length) return false;
  return formList.every((form) => (percentFor || defaultPercent)(forms, form, visitId) === 100);
}

/** Phase flags stored with the chart. Keys match pipeline stage ids. */
export function phaseFlagsForOutline(outline, forms, options = {}) {
  const percentFor = options.percentFor
    ? (savedForms, form, visitId) => options.percentFor(form, visitId, savedForms)
    : defaultPercent;
  const flags = {};
  (outline || []).forEach((phase) => {
    if (phase.visits) {
      phase.visits.forEach((visit) => {
        if (visit.fromDay == null || visit.throughDay == null) return;
        flags[visit.id] = sectionComplete(forms, visit.forms, visit.id, percentFor);
      });
      return;
    }
    if (!PIPELINE_PHASES.has(phase.id)) return;
    flags[phase.id] = sectionComplete(forms, phase.forms, phase.visitId || '', percentFor);
  });
  return flags;
}

/** Surgery date entered on the operative form, when the chart has one. */
export function chartSurgeryDate(state) {
  const values = state?.forms?.['operative::case']?.values || {};
  const raw = values['alliance.index.date'] || values.surgery_date || '';
  const match = String(raw).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
}

/**
 * Where one patient sits in one stage.
 * Enrollment uses incomplete, complete, and denied.
 * Later stages use current when the step needs action now, upcoming when the date is still ahead,
 * and completed when the step is done. null means the stage does not show them.
 */
export function stageMembership(stage, patient, today = new Date()) {
  const phases = patient?.phases && typeof patient.phases === 'object' ? patient.phases : {};
  const declined = Boolean(patient?.declined);
  const complete = Boolean(phases[stage.id]);
  const surgery = parseSurgeryDay(patient?.surgeryDate || patient?.surgery_date);
  const day = startOfDay(today);

  if (stage.kind === 'enrollment') {
    if (declined) return 'denied';
    if (complete) return 'complete';
    return 'incomplete';
  }
  if (declined || !phases.enrollment) return null;
  if (complete) return 'completed';

  if (stage.kind === 'preop') {
    if (!surgery) return 'current';
    const last = addDays(day, stage.withinDays);
    if (surgery <= last) return 'current';
    return 'upcoming';
  }
  if (!surgery) return null;
  if (stage.kind === 'surgery') {
    return surgery.getTime() <= day.getTime() ? 'current' : 'upcoming';
  }
  if (stage.kind === 'followup') {
    const delta = daysAfterSurgery(surgery, day);
    if (delta >= stage.fromDay) return 'current';
    const priors = stage.priorIds || [];
    if (priors.some((id) => !phases[id])) return null;
    return 'upcoming';
  }
  if (stage.kind === 'exit') {
    if (!phases[stage.afterId]) return null;
    return 'current';
  }
  return null;
}

function surgerySortValue(patient) {
  return parseSurgeryDay(patient?.surgeryDate || patient?.surgery_date);
}

function bySurgeryDate(a, b) {
  const left = surgerySortValue(a);
  const right = surgerySortValue(b);
  if (!left && !right) return String(a?.name || '').localeCompare(String(b?.name || ''));
  if (!left) return -1;
  if (!right) return 1;
  return left - right || String(a?.name || '').localeCompare(String(b?.name || ''));
}

/** Patients on a step, split into work that is due and dates that are still ahead. */
export function stageLists(stage, patients, today = new Date()) {
  const action = [];
  const upcoming = [];
  (patients || []).forEach((patient) => {
    const place = stageMembership(stage, patient, today);
    if (place === 'current' || place === 'incomplete') action.push(patient);
    else if (place === 'upcoming') upcoming.push(patient);
  });
  action.sort(bySurgeryDate);
  upcoming.sort(bySurgeryDate);
  return { action, upcoming };
}

export function patientsInStage(stage, patients, tab, today = new Date()) {
  return (patients || []).filter((patient) => stageMembership(stage, patient, today) === tab);
}

/** True only when every pipeline stage is finished. One finished step is not enough. */
export function allStagesComplete(stages, patient) {
  if (!patient || patient.declined) return false;
  const list = stages || [];
  if (!list.length) return false;
  const phases = patient.phases && typeof patient.phases === 'object' ? patient.phases : {};
  return list.every((stage) => Boolean(phases[stage.id]));
}
